"use client";

import { useCallback, useSyncExternalStore } from "react";

const CHANGE_EVENT = "tradesfolk-storage-change";

function subscribe(onChange: () => void) {
  window.addEventListener("storage", onChange);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
}

/** Stable server snapshot avoids hydration mismatches for browser preferences. */
export function useStoredValue(key: string, fallback: string, serverValue: string | null) {
  const getSnapshot = useCallback(() => localStorage.getItem(key) ?? fallback, [key, fallback]);
  const getServerSnapshot = useCallback(() => serverValue, [serverValue]);
  const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  function setValue(next: string | null) {
    if (next === null) localStorage.removeItem(key);
    else localStorage.setItem(key, next);
    // The native storage event only fires in other tabs.
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }

  return [value, setValue] as const;
}
